import React, { useState, useEffect } from 'react';
import { auth } from '../firebase';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation, getFirebaseAuthError } from '../translations';

const PasswordResetPage = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState('');
  const { language } = useLanguage();
  const [oobCode, setOobCode] = useState(null);

  useEffect(() => {
    // Extract parameters from URL
    const urlParams = new URLSearchParams(window.location.search);
    const mode = urlParams.get('mode');
    const code = urlParams.get('oobCode');

    if (mode !== 'resetPassword' || !code) {
      setError(getTranslation(language, 'invalidResetLink') || 'Invalid or missing password reset link.');
      setVerifying(false);
      return;
    }

    setOobCode(code);

    // Verify the code and get the email
    verifyPasswordResetCode(auth, code)
      .then((email) => {
        setEmail(email);
        setVerifying(false);
      })
      .catch((err) => {
        setError(getFirebaseAuthError(err, language));
        setVerifying(false);
      });
  }, [language]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation
    if (!newPassword) {
      setError(getTranslation(language, 'pleaseFillAllFields') || 'Please enter your new password.');
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError(getTranslation(language, 'passwordTooShort') || 'Password must be at least 6 characters long.');
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(getTranslation(language, 'passwordsDoNotMatch') || 'Passwords do not match.');
      setLoading(false);
      return;
    }

    if (!oobCode) {
      setError(getTranslation(language, 'invalidResetLink') || 'Invalid reset code.');
      setLoading(false);
      return;
    }

    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      setSuccess(true);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(getFirebaseAuthError(err, language));
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="site-wrap">
        <div className="intro-section" style={{ 
          background: 'linear-gradient(135deg, #000000 0%, #333333 100%)',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px 0'
        }}>
          <div className="container-fluid" style={{ paddingLeft: '60px', paddingRight: '60px' }}>
            <div className="row" style={{ minHeight: '100vh', alignItems: 'center', justifyContent: 'center' }}>
              <div className="col-lg-5">
                <div className="bg-white p-5" style={{ 
                  borderRadius: '15px',
                  boxShadow: '0 25px 80px rgba(0, 0, 0, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  maxWidth: '450px',
                  margin: '0 auto',
                  textAlign: 'center'
                }}>
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ 
                      width: '50px', 
                      height: '50px', 
                      border: '4px solid #f3f3f3',
                      borderTop: '4px solid #000',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      margin: '0 auto'
                    }}></div>
                  </div>
                  <p style={{ color: '#666', fontSize: '16px', margin: 0 }}>
                    {getTranslation(language, 'verifyingLink') || 'Verifying link...'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (success) {
    return (
      <div className="site-wrap">
        <div className="intro-section" style={{ 
          background: 'linear-gradient(135deg, #000000 0%, #333333 100%)',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px 0'
        }}>
          <div className="container-fluid" style={{ paddingLeft: '60px', paddingRight: '60px' }}>
            <div className="row" style={{ minHeight: '100vh', alignItems: 'center', justifyContent: 'center' }}>
              <div className="col-lg-5">
                <div className="bg-white p-5" style={{ 
                  borderRadius: '15px',
                  boxShadow: '0 25px 80px rgba(0, 0, 0, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  maxWidth: '450px',
                  margin: '0 auto'
                }}>
                  <div className="text-center mb-4">
                    <div style={{ marginBottom: '15px' }}>
                      <img 
                        src="/gymgm_logo.jpg" 
                        alt="GymGM Logo" 
                        style={{
                          width: '70px',
                          height: 'auto',
                          borderRadius: '10px',
                          boxShadow: '0 5px 15px rgba(0, 0, 0, 0.2)'
                        }}
                      />
                    </div>
                    <h1 className="site-logo" style={{ fontSize: '2.3rem', fontWeight: '900', color: '#000', marginBottom: '8px' }}>
                      GymGM
                    </h1>
                  </div>

                  <div className="alert alert-success" style={{
                    padding: '16px',
                    borderRadius: '8px',
                    border: '1px solid #c3e6cb',
                    background: '#d4edda',
                    color: '#155724',
                    fontSize: '16px',
                    marginBottom: '20px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '10px' }}>✓</div>
                    <div style={{ fontWeight: '600', marginBottom: '8px' }}>
                      {getTranslation(language, 'passwordResetSuccessful') || 'Password Reset Successful!'}
                    </div>
                    <div style={{ fontSize: '14px' }}>
                      {getTranslation(language, 'passwordResetSuccessMessage') || 'Your password has been successfully changed. You can now log in with your new password.'}
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      window.location.href = '/';
                    }}
                    className="btn btn-black"
                    style={{
                      width: '100%',
                      height: '50px',
                      fontSize: '16px',
                      fontWeight: '700',
                      textTransform: 'uppercase',
                      borderRadius: '8px',
                      border: '2px solid #000',
                      background: '#000',
                      color: '#fff',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {getTranslation(language, 'backToLogin') || 'Back to Login'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="site-wrap">
      <div className="intro-section" style={{ 
        background: 'linear-gradient(135deg, #000000 0%, #333333 100%)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px 0',
        position: 'relative'
      }}>
        {/* Language Selector - Top Right */}
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 1000
        }}>
          <select
            value={language}
            onChange={(e) => {
              // Language change handled by context
              window.location.reload();
            }}
            style={{
              padding: '10px 16px',
              fontSize: '14px',
              borderRadius: '8px',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              background: 'rgba(255, 255, 255, 0.1)',
              color: '#fff',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              backdropFilter: 'blur(10px)',
              fontWeight: '500'
            }}
          >
            <option value="en" style={{ background: '#000', color: '#fff' }}>English</option>
            <option value="tr" style={{ background: '#000', color: '#fff' }}>Türkçe</option>
          </select>
        </div>

        <div className="container-fluid" style={{ paddingLeft: '60px', paddingRight: '60px' }}>
          <div className="row" style={{ minHeight: '100vh', alignItems: 'center', justifyContent: 'center' }}>
            <div className="col-lg-5">
              <div className="bg-white p-5" style={{ 
                borderRadius: '15px',
                boxShadow: '0 25px 80px rgba(0, 0, 0, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                maxWidth: '450px',
                margin: '0 auto',
                marginTop: '70px'
              }}>
                {/* Logo/Header */}
                <div className="text-center mb-4">
                  <div style={{ marginBottom: '15px' }}>
                    <img 
                      src="/gymgm_logo.jpg" 
                      alt="GymGM Logo" 
                      style={{
                        width: '70px',
                        height: 'auto',
                        borderRadius: '10px',
                        boxShadow: '0 5px 15px rgba(0, 0, 0, 0.2)'
                      }}
                    />
                  </div>
                  <h1 className="site-logo" style={{ fontSize: '2.3rem', fontWeight: '900', color: '#000', marginBottom: '8px' }}>
                    GymGM
                  </h1>
                  <p className="text-muted" style={{ fontSize: '1rem', marginTop: '5px' }}>
                    {getTranslation(language, 'setNewPassword') || 'Set New Password'}
                  </p>
                  {email && (
                    <p style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
                      {getTranslation(language, 'email') || 'Email'}: {email}
                    </p>
                  )}
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit}>
                  <div className="form-group mb-3">
                    <label style={{ 
                      fontSize: '14px', 
                      fontWeight: '600', 
                      color: '#000',
                      marginBottom: '8px',
                      display: 'block'
                    }}>
                      {getTranslation(language, 'newPassword') || 'New Password'}
                    </label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="form-control"
                      style={{
                        height: '50px',
                        borderRadius: '8px',
                        border: '2px solid #e0e0e0',
                        fontSize: '16px',
                        padding: '12px 16px'
                      }}
                      required
                      minLength={6}
                      placeholder={language === 'tr' ? 'En az 6 karakter' : 'At least 6 characters'}
                    />
                  </div>

                  <div className="form-group mb-4">
                    <label style={{ 
                      fontSize: '14px', 
                      fontWeight: '600', 
                      color: '#000',
                      marginBottom: '8px',
                      display: 'block'
                    }}>
                      {getTranslation(language, 'confirmPassword') || 'Confirm Password'}
                    </label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="form-control"
                      style={{
                        height: '50px',
                        borderRadius: '8px',
                        border: '2px solid #e0e0e0',
                        fontSize: '16px',
                        padding: '12px 16px'
                      }}
                      required
                      minLength={6}
                      placeholder={language === 'tr' ? 'Şifreyi tekrar girin' : 'Re-enter password'}
                    />
                  </div>

                  {error && (
                    <div className="alert alert-danger" style={{
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '1px solid #f5c6cb',
                      background: '#f8d7da',
                      color: '#721c24',
                      fontSize: '14px',
                      marginBottom: '20px'
                    }}>
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn btn-black"
                    style={{
                      width: '100%',
                      height: '50px',
                      fontSize: '16px',
                      fontWeight: '700',
                      textTransform: 'uppercase',
                      borderRadius: '8px',
                      border: '2px solid #000',
                      background: loading ? '#ccc' : '#000',
                      color: '#fff',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      opacity: loading ? 0.7 : 1
                    }}
                  >
                    {loading ? 
                      (getTranslation(language, 'resetting') || 'Resetting...') : 
                      (getTranslation(language, 'resetPassword') || 'Reset Password')
                    }
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetPage;

